// The Round bench end tool and a wall bench's Rounded ends card: a free end of a
// wall bench swept round the point where its end edge meets the wall, every tier
// ending there at once (polyRoom.ts `polyBenchRoundEnds`).
// docs/claude/benches.md, *Rounded ends*.
import { type ProductInstance } from '../../shared/types';
import {
  type BenchRoundEnd,
  isPolyRoom,
  polyBenchRoundEnds,
} from '../polyRoom';
import {
  supportFrame,
  supportHoverPolys,
} from './benchSupportTool';
import {
  instLocked,
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import { pointInPolygon } from '../geometry';
import {
  aimedBench,
  pxAt,
  type ToolAim,
} from '../toolAim';
import { distToPolyline } from '../modifyOps';
import { tr } from '../i18n';
import {
  checkboxRow,
  sectionTitle,
} from './panelRows';
import {
  selectFeature,
  selectOne,
} from '../selection';
import {
  type BenchHover,
  deselectAll,
  instancesAt,
} from '../main';

/** How far from a bench's end (mm) the tool still takes it. */
const ROUND_REACH = 150;

/** Every end rounded round the same wall point as `end`: the tiers one click
 *  rounds together. */
function stackAt(inst: ProductInstance, end: BenchRoundEnd): BenchRoundEnd[] {
  const key = end.piece.roundEnd?.key;
  return polyBenchRoundEnds(inst).filter((r) => r.piece.roundEnd?.key === key);
}

/** Round every tier ending at `end`'s wall point, or square them again. A
 *  rounded end is never closed too. Inside the caller's undo step. */
export function setRoundEnd(inst: ProductInstance, end: BenchRoundEnd, round: boolean): void {
  for (const r of stackAt(inst, end)) {
    const pb = inst.polyBenches?.find((b) => b.id === r.bench);
    if (!pb) continue;
    const [k, close] = r.side === 'start' ? (['roundStart', 'closeStart'] as const) : (['roundEnd', 'closeEnd'] as const);
    if (round) { pb[k] = true; delete pb[close]; } else delete pb[k];
  }
}

/** The tool's target: the end that can round nearest the cursor, on its round
 *  piece or within reach of its outline — in 3D, of the bench the click is on. */
function benchRoundTargetAt(aim: ToolAim): { inst: ProductInstance; end: BenchRoundEnd } | null {
  const world = aim.world;
  const reach = Math.max(ROUND_REACH, pxAt(aim, 8));
  for (const id of instancesAt(world, false)) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (!inst || instLocked(inst) || !isPolyRoom(inst) || !inst.autoBenches) continue;
    const p = supportFrame(inst, world);
    const on = aimedBench(aim);
    let best: BenchRoundEnd | null = null, bestD = reach;
    for (const r of polyBenchRoundEnds(inst)) {
      if (on && r.bench !== on) continue;
      const d = pointInPolygon(p, r.piece.poly) ? 0 : distToPolyline(p, r.piece.poly, true);
      if (d < bestD || (d === bestD && best && r.tier > best.tier)) { bestD = d; best = r; }
    }
    if (best) return { inst, end: best };
  }
  return null;
}

/** The hover: the round pieces a click adds (green) or takes away (red). */
export function benchRoundHoverAt(aim: ToolAim): BenchHover | null {
  const t = benchRoundTargetAt(aim);
  if (!t) return null;
  const polys = stackAt(t.inst, t.end).map((r) => r.piece.poly);
  return { instId: t.inst.id, polys: supportHoverPolys(t.inst, polys), kind: t.end.rounded ? 'delete' : 'add' };
}

export function downBenchRoundTool(e: MouseEvent, aim: ToolAim): boolean {
  if (state.tool !== 'bench-round' || e.button !== 0) return false;
  const t = benchRoundTargetAt(aim);
  if (!t) return true;
  pushHistory();
  setRoundEnd(t.inst, t.end, !t.end.rounded);
  deselectAll();
  selectOne(state, 'instance', t.inst.id);
  selectFeature(state, { kind: 'bench', instId: t.inst.id, benchId: t.end.bench });
  state.benchAddHover = benchRoundHoverAt(aim);
  renderProperties();
  invalidate();
  return true;
}

/** A bench's Rounded ends card: Round start, Round end — each greyed where the
 *  end cannot round (another bench, a partition or a corner carries on), both on
 *  a partition's bench (`onWall` false), which has no wall to round against: the
 *  card is there all the same, one panel body for both hosts. */
export function appendBenchRoundCard(body: HTMLElement, inst: ProductInstance, bench: string, onWall = true): void {
  const ends = onWall ? polyBenchRoundEnds(inst).filter((r) => r.bench === bench) : [];
  body.appendChild(sectionTitle('Rounded ends'));
  for (const side of ['start', 'end'] as const) {
    const end = ends.find((r) => r.side === side);
    const row = checkboxRow(side === 'start' ? 'Round start' : 'Round end', !!end?.rounded, (v) => {
      if (!end) return;
      pushHistory();
      setRoundEnd(inst, end, v);
      renderProperties();
      invalidate();
    });
    if (!end) {
      row.querySelector('input')!.disabled = true;
      row.title = tr(onWall ? 'This end cannot round: it runs along a wall, or another bench carries on from it' : 'Only a wall bench’s end rounds');
    }
    body.appendChild(row);
  }
}
